import { DatePipe } from "@angular/common";
import { Injectable, OnDestroy, inject } from "@angular/core";
import {
    LG_APP_INFO,
    LG_APP_SESSION,
    LG_AUTHENTICATION_SERVICE,
    LG_AUTHORIZATION_SERVICE,
    LG_USERFLOW_SERVICE,
    LG_USER_INFO,
    LgAuthorizationServiceV2,
    LgMatTrackingService,
    OidcAuthService,
    UserSettingsService
} from "@logex/framework/lg-application";
import * as _ from "lodash";
import { Subscription, lastValueFrom } from "rxjs";

import { AppSession } from "@shared/app-session";
import { AppScenario } from "@shared/scenario/app-scenario";
import { ScenarioService } from "@shared/scenario/scenario-service";
import { BootHelperGateway, Client } from "./boot-helper-gateway";

import {
    LG_APPLICATION_MULTI_EVENT_TRACERS,
    LgApplicationEventTracerNames
} from "@logex/framework/core";
import { rebuildNavigation } from "@shared/app-navigation";
import { ScenarioSettingsService } from "@shared/scenario/scenario-settings-service";
import { MatomoConfiguration } from "@shared/services/matomo-insights.configuration";

@Injectable({ providedIn: "root" })
export class BootHelperService implements OnDestroy {
    _bootHelperGateway = inject(BootHelperGateway);
    userflowService = inject(LG_USERFLOW_SERVICE);
    private _appInfo = inject(LG_APP_INFO);
    private _appSession = inject<AppSession>(LG_APP_SESSION);
    private _authorizationService = inject<LgAuthorizationServiceV2>(LG_AUTHORIZATION_SERVICE);
    private _datePipe = inject(DatePipe);
    private _oidcAuthService = inject<OidcAuthService>(LG_AUTHENTICATION_SERVICE);
    private _scenarioService = inject(ScenarioService<AppScenario>);
    private _scenarioSettingsService: ScenarioSettingsService = inject(ScenarioSettingsService);
    private _tracers = inject(LG_APPLICATION_MULTI_EVENT_TRACERS);
    private _userInfo = inject(LG_USER_INFO);
    private _userSettingsService = inject(UserSettingsService);
    private _userflowSubscription: Subscription | null = null;

    ngOnDestroy(): void {
        this._userflowSubscription?.unsubscribe();
    }

    isSessionConfigured(): boolean {
        return this._appSession.scenarioId != null;
    }

    async trySelectLastSession(): Promise<boolean> {
        if (this.isSessionConfigured()) return true;

        // Get last client and scenario
        let clientId, scenarioId;
        try {
            const selectedScenario = await lastValueFrom(
                this._userSettingsService.get({ storageId: "$context" })
            );
            clientId = selectedScenario[0].value.clientId;
            scenarioId = selectedScenario[0].value.scenarioId;
        } catch {}

        // Try to open the same scenario
        if (clientId != null /* && scenarioId != null*/) {
            const scenario = await this.loadPreferredScenario(clientId, scenarioId);

            if (scenario == null) return false;

            return await this.setSessionScenario(scenario);
        }

        return false;
    }

    async loadPreferredScenario(clientId: number, scenarioId: number): Promise<AppScenario> {
        await this._scenarioSettingsService.load(clientId, scenarioId);
        return this._scenarioService.load(clientId, scenarioId);
    }

    async setSessionScenario(scenario: AppScenario): Promise<boolean> {
        // Get client info by scenario
        // TODO: Refactor to get data directly from a special endpoint
        const clients = await lastValueFrom(this._bootHelperGateway.selectClients());
        const client = _.find(clients, { id: scenario.clientId });

        // Requested client is not available (different DB, data change, etc)
        if (client == null) return false;

        this._appSession.scenarioId = scenario.id;
        this._appSession.clientId = scenario.clientId;

        this._appSession.client = {
            id: client.id,
            code: client.code,
            name: client.name,
            abbr: client.abbreviation
        };

        const startDate = new Date(scenario.startDate);
        const startMonthName = this._datePipe.transform(
            new Date(startDate.getFullYear(), startDate.getMonth()),
            "MMMM"
        );
        const endDate = new Date(scenario.endDate);
        const endMonthName = this._datePipe.transform(
            new Date(endDate.getFullYear(), endDate.getMonth()),
            "MMMM"
        );

        this._appSession.scenario = {
            id: scenario.id,
            clientId: client.id,
            description: scenario.description,
            date: new Date(scenario.date),
            startDate,
            endDate,
            period: `${startMonthName} ${startDate.getFullYear()} - ${endMonthName} ${endDate.getFullYear()}`,
            year: scenario.year,
            month: scenario.month,
            isLocked: scenario.isLocked,
            dotRelease: scenario.dotRelease,
            hospitalCode: scenario.hospitalCode,
            isDefault: scenario.isDefault,
            quarter: scenario.quarter,
            name: scenario.description,
            isPublished: scenario.isPublished
        };

        await this._fillUserInfo(client);

        const aiTracer = this._tracers.find(
            x => x.tracerName === LgApplicationEventTracerNames.Matomo
        );
        if (aiTracer != null && aiTracer instanceof LgMatTrackingService) {
            aiTracer.init(new MatomoConfiguration(this._appInfo, this._appSession, this._userInfo));
        }
        this._userflowSubscription = this.userflowService.ready$.subscribe(() => {
            this.userflowService.patchUserAttrs({
                name: this._userInfo.name,
                email: this._oidcAuthService.user.login,
                organization: this._userInfo.ziekenhuiscode.toString()
            });
        });

        return true;
    }

    private async _fillUserInfo(client: Client): Promise<void> {
        // For compatibility with LgSidebarDevWidgetComponent
        this._userInfo.ziekenhuiscode = client.code as any;
        this._userInfo.ziekenhuisnaam = client.name;

        const perms = await lastValueFrom(this._authorizationService.getUserPermissions());

        perms.forEach(x => {
            this._userInfo.roles[x] = true;
        });

        rebuildNavigation();
    }
}
